import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import isEqual from "lodash/isEqual";
import { Button, Input, Modal } from "@zorni/components-lib";
import {
  GET_TRIP_PUBLICATION,
  PUBLISH_TRIP,
  publishTrip,
  UPDATE_TRIP_PUBLICATION,
  updateTripPublication,
} from "./PublishTripAPI";
import { useAdmin } from "../../../hooks/useAdmin";
import { useOperation } from "../../../hooks/useOperation";
import styles from "./PublishTrip.module.css";
import PublishTripHeader from "./components/PublishTripHeader";
import GeneralSettingsSection from "./components/GeneralSettingsSection";
import TripDateFormSection from "./components/TripDateFormSection";
import TripDatesListSection from "./components/TripDatesListSection";
import PublishTripActions from "./components/PublishTripActions";
import {
  getRangeLength,
  hasInvalidManagerPhone,
  isTripDateComplete,
  numberValue,
} from "./utils/publishTrip.helpers";

const DEFAULT_PHONE_INDICATIVE = "212";
const normalizeIndicative = (indicative) => {
  const normalized = String(indicative || "").trim();
  if (!normalized || normalized === "+00") return DEFAULT_PHONE_INDICATIVE;
  return normalized;
};

const EMPTY_FORM = {
  startDate: "",
  endDate: "",
  adultPrice: "",
  childPrice: "",
  babyPrice: "",
  adultSeats: "",
  childSeats: "",
  babySeats: "",
  isCheapestDate: false,
  stepsRows: [],
  travelManagers: [],
};

const EMPTY_MANAGER = {
  fullName: "",
  email: "",
  phoneNumber: {
    indicative: DEFAULT_PHONE_INDICATIVE,
    number: "",
  },
};

const EMPTY_TRIP_DATE = {
  startDate: "",
  endDate: "",
  nbDays: 0,
  nbNight: 0,
  isCheapestDate: false,
  isFlightIncluded: false,
  adultPricePerPerson: {
    value: 0,
    currency: "DH",
  },
  childPricePerPerson: {
    value: 0,
    currency: "DH",
  },
  babiesPricePerPerson: {
    value: 0,
    currency: "DH",
  },
  stepsDate: [],
  flightItinerary: {
    outboundFlight: {
      isFlightWithStopover: false,
      flights: [],
    },
    returnFlight: {
      isFlightWithStopover: false,
      flights: [],
    },
  },
  seatsAvailable: {
    initialNumberOfSeatsAvailable: 0,
    initialNumberOfChildrensSeatsAvailable: 0,
    initialNumberOfBabiessSeatsAvailable: 0,
    numberOfSeatsAvailable: 0,
    numberOfChildrensSeatsAvailable: 0,
    numberOfBabiessSeatsAvailable: 0,
  },
  travelManagers: [],
};

const buildStepsDate = (rows) => {
  const grouped = rows.reduce((acc, row) => {
    const date = (row.date || "").slice(0, 10);
    const stepId = (row.stepId || "").trim();

    if (!date || !stepId) return acc;

    if (!acc[date]) {
      acc[date] = new Set();
    }

    acc[date].add(stepId);
    return acc;
  }, {});

  return Object.entries(grouped).map(([date, stepIdsSet]) => ({
    date,
    stepIds: Array.from(stepIdsSet),
  }));
};

const tripDateToForm = (tripDate) => {
  const stepsRows = (tripDate.stepsDate || []).flatMap((entry) =>
    (entry.stepIds || []).map((stepId) => ({ date: entry.date, stepId }))
  );
  const travelManagers = (tripDate.travelManagers || []).map((manager) => ({
    fullName: manager?.fullName || "",
    email: manager?.email || "",
    phoneNumber: {
      indicative: normalizeIndicative(manager?.phoneNumber?.indicative),
      number: manager?.phoneNumber?.number || "",
    },
  }));

  return {
    startDate: (tripDate.startDate || "").slice(0, 10),
    endDate: (tripDate.endDate || "").slice(0, 10),
    adultPrice: String(numberValue(tripDate.adultPricePerPerson)),
    childPrice: String(numberValue(tripDate.childPricePerPerson)),
    babyPrice: String(numberValue(tripDate.babiesPricePerPerson)),
    adultSeats: String(
      Number(
        tripDate?.seatsAvailable?.initialNumberOfSeatsAvailable ??
          tripDate?.SeatsAvailable?.initialNumberOfSeatsAvailable ??
          0
      )
    ),
    childSeats: String(
      Number(
        tripDate?.seatsAvailable?.initialNumberOfChildrensSeatsAvailable ??
          tripDate?.SeatsAvailable?.initialNumberOfChildrensSeatsAvailable ??
          0
      )
    ),
    babySeats: String(
      Number(
        tripDate?.seatsAvailable?.initialNumberOfBabiessSeatsAvailable ??
          tripDate?.SeatsAvailable?.initialNumberOfBabiessSeatsAvailable ??
          0
      )
    ),
    isCheapestDate: Boolean(tripDate.isCheapestDate),
    stepsRows,
    travelManagers,
  };
};

const cleanTripDates = (tripDates) =>
  tripDates.map((date) => {
    const { seatsAvailable, SeatsAvailable: upperSeatsAvailable, ...rest } = date;

    return {
      ...rest,
      flightItinerary: {
        outboundFlight: {
          isFlightWithStopover: Boolean(
            date?.flightItinerary?.outboundFlight?.isFlightWithStopover
          ),
          flights: Array.isArray(date?.flightItinerary?.outboundFlight?.flights)
            ? date.flightItinerary.outboundFlight.flights
            : [],
        },
        returnFlight: {
          isFlightWithStopover: Boolean(
            date?.flightItinerary?.returnFlight?.isFlightWithStopover
          ),
          flights: Array.isArray(date?.flightItinerary?.returnFlight?.flights)
            ? date.flightItinerary.returnFlight.flights
            : [],
        },
      },
      adultPricePerPerson:
        typeof date.adultPricePerPerson === "object"
          ? {
              value: Number(date.adultPricePerPerson.value ?? 0),
              currency: date.adultPricePerPerson.currency ?? "DH",
            }
          : { value: Number(date.adultPricePerPerson ?? 0), currency: "DH" },
      childPricePerPerson:
        typeof date.childPricePerPerson === "object"
          ? {
              value: Number(date.childPricePerPerson.value ?? 0),
              currency: date.childPricePerPerson.currency ?? "DH",
            }
          : { value: Number(date.childPricePerPerson ?? 0), currency: "DH" },
      babiesPricePerPerson:
        typeof date.babiesPricePerPerson === "object"
          ? {
              value: Number(date.babiesPricePerPerson.value ?? 0),
              currency: date.babiesPricePerPerson.currency ?? "DH",
            }
          : { value: Number(date.babiesPricePerPerson ?? 0), currency: "DH" },
      seatsAvailable: {
        initialNumberOfSeatsAvailable: Number(
          seatsAvailable?.initialNumberOfSeatsAvailable ??
            upperSeatsAvailable?.initialNumberOfSeatsAvailable ??
            0
        ),
        initialNumberOfChildrensSeatsAvailable: Number(
          seatsAvailable?.initialNumberOfChildrensSeatsAvailable ??
            upperSeatsAvailable?.initialNumberOfChildrensSeatsAvailable ??
            0
        ),
        initialNumberOfBabiesSeatsAvailable: Number(
          seatsAvailable?.initialNumberOfBabiesSeatsAvailable ??
            upperSeatsAvailable?.initialNumberOfBabiesSeatsAvailable ??
            seatsAvailable?.initialNumberOfBabiessSeatsAvailable ??
            upperSeatsAvailable?.initialNumberOfBabiessSeatsAvailable ??
            0
        ),
        numberOfSeatsAvailable: Number(
          seatsAvailable?.numberOfSeatsAvailable ??
            upperSeatsAvailable?.numberOfSeatsAvailable ??
            0
        ),
        numberOfChildrensSeatsAvailable: Number(
          seatsAvailable?.numberOfChildrensSeatsAvailable ??
            upperSeatsAvailable?.numberOfChildrensSeatsAvailable ??
            0
        ),
        numberOfBabiesSeatsAvailable: Number(
          seatsAvailable?.numberOfBabiesSeatsAvailable ??
            upperSeatsAvailable?.numberOfBabiesSeatsAvailable ??
            seatsAvailable?.numberOfBabiessSeatsAvailable ??
            upperSeatsAvailable?.numberOfBabiessSeatsAvailable ??
            0
        ),
      },
    };
  });

const cleanObject = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(cleanObject);
  }

  if (obj !== null && typeof obj === "object") {
    const newObj = {};

    for (const key in obj) {
      if (
        key === "__typename" ||
        key === "establishment" ||
        key === "tripProgramDetails" ||
        key === "title" ||
        key === "reference"
      ) {
        continue;
      }

      const cleanedValue = cleanObject(obj[key]);

      if (key !== "trip") {
        newObj[key] = cleanedValue;
      } else {
        for (const innerKey in cleanedValue) {
          if (innerKey === "id") {
            newObj.tripId = cleanedValue[innerKey];
          } else {
            newObj[innerKey] = cleanedValue[innerKey];
          }
        }
      }
    }

    return newObj;
  }

  return obj;
};

const PublishTrip = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { publicationId } = useParams();
  const { accessToken } = useAdmin();
  const { runOperation, showToast, triggerRefresh } = useOperation();

  const { data, loading, error } = useQuery(GET_TRIP_PUBLICATION, {
    variables: { tripPublicationId: publicationId },
    fetchPolicy: "network-only",
  });

  const [publishTripMutation] = useMutation(PUBLISH_TRIP);
  const [updateTripPublicationMutation] = useMutation(UPDATE_TRIP_PUBLICATION);

  const [trip, setTrip] = useState({});
  const [listTripDates, setListTripDates] = useState([]);
  const [maxChildAge, setMaxChildAge] = useState("");
  const [maxBabyAge, setMaxBabyAge] = useState("");
  const [initialSnapshot, setInitialSnapshot] = useState({
    maxChildAge: "",
    maxBabyAge: "",
    tripDates: [],
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formResetKey, setFormResetKey] = useState(0);
  const [pendingNewIndex, setPendingNewIndex] = useState(null);
  const [isUseTemplateModalOpen, setIsUseTemplateModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");

  useEffect(() => {
    if (!data?.QR_GET_TRIP_PUBLICATION?.data) return;

    const newTrip = data.QR_GET_TRIP_PUBLICATION.data;
    const initialChildAge =
      newTrip.childsMaxAge === null || newTrip.childsMaxAge === undefined
        ? ""
        : String(newTrip.childsMaxAge);
    const initialBabyAge =
      newTrip.babiesMaxAge === null || newTrip.babiesMaxAge === undefined
        ? ""
        : String(newTrip.babiesMaxAge);
    const initialTripDates = newTrip.tripDates || [];

    setTrip(newTrip);
    setListTripDates(initialTripDates);
    setMaxChildAge(initialChildAge);
    setMaxBabyAge(initialBabyAge);
    setInitialSnapshot({
      maxChildAge: initialChildAge,
      maxBabyAge: initialBabyAge,
      tripDates: initialTripDates,
    });
  }, [data]);

  const stepOptions = useMemo(
    () => trip?.trip?.tripProgramDetails || [],
    [trip?.trip?.tripProgramDetails]
  );

  const duration = useMemo(
    () => getRangeLength(form.startDate, form.endDate),
    [form.startDate, form.endDate]
  );

  const isAnyTripDateComplete = useMemo(
    () =>
      listTripDates.length > 0 &&
      listTripDates.every((tripDate) =>
        isTripDateComplete(tripDate, trip?.trip?.tripProgramDetails)
      ),
    [listTripDates, trip?.trip?.tripProgramDetails]
  );

  const showEditNewStepAlert = useMemo(() => {
    const isFromTripEdit = Boolean(location?.state?.fromTripEdit);
    if (!isFromTripEdit) return false;

    const hasStepStructureChangedFromEdit = Boolean(location?.state?.hasStepStructureChanged);
    if (!hasStepStructureChangedFromEdit) return false;

    const hasDepartureInstances = (listTripDates || []).length > 0;
    return hasDepartureInstances;
  }, [location?.state, listTripDates]);

  const hasChanges = useMemo(() => {
    if (!trip?.id) return false;
    const currentChildAge =
      maxChildAge === null || maxChildAge === undefined ? "" : String(maxChildAge).trim();
    const currentBabyAge =
      maxBabyAge === null || maxBabyAge === undefined ? "" : String(maxBabyAge).trim();

    return !isEqual(
      {
        maxChildAge: currentChildAge,
        maxBabyAge: currentBabyAge,
        tripDates: listTripDates,
      },
      initialSnapshot
    );
  }, [trip?.id, maxChildAge, maxBabyAge, listTripDates, initialSnapshot]);

  const setFormField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, stepsRows: [] });
    setEditingIndex(null);
    setFormResetKey((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNewDeparture = () => {
    const newTripDate = {
      ...EMPTY_TRIP_DATE,
      adultPricePerPerson: { ...EMPTY_TRIP_DATE.adultPricePerPerson },
      childPricePerPerson: { ...EMPTY_TRIP_DATE.childPricePerPerson },
      babiesPricePerPerson: { ...EMPTY_TRIP_DATE.babiesPricePerPerson },
      seatsAvailable: { ...EMPTY_TRIP_DATE.seatsAvailable },
      stepsDate: [],
      travelManagers: [],
      flightItinerary: {
        outboundFlight: { ...EMPTY_TRIP_DATE.flightItinerary.outboundFlight, flights: [] },
        returnFlight: { ...EMPTY_TRIP_DATE.flightItinerary.returnFlight, flights: [] },
      },
    };

    const nextIndex = listTripDates.length;
    setListTripDates((prev) => [...prev, newTripDate]);
    setPendingNewIndex(nextIndex);
    setEditingIndex(nextIndex);
    setForm({ ...EMPTY_FORM, stepsRows: [] });
    setFormResetKey((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addStepRow = () => {
    if (!form.startDate || !form.endDate) {
      showToast(t("tripPublication.publishTrip.toasts.requiredDates"), "error");
      return;
    }

    setForm((prev) => ({
      ...prev,
      stepsRows: [...prev.stepsRows, { date: "", stepId: "" }],
    }));
  };

  const addManagerRow = () => {
    setForm((prev) => ({
      ...prev,
      travelManagers: [
        ...(prev.travelManagers || []),
        {
          ...EMPTY_MANAGER,
          phoneNumber: { ...EMPTY_MANAGER.phoneNumber },
        },
      ],
    }));
  };

  const updateManagerRow = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      travelManagers: (prev.travelManagers || []).map((manager, managerIndex) =>
        managerIndex === index
          ? {
              ...manager,
              [key]:
                key === "phoneNumber"
                  ? {
                      indicative: normalizeIndicative(manager?.phoneNumber?.indicative),
                      ...value,
                    }
                  : value,
            }
          : manager
      ),
    }));
  };

  const deleteManagerRow = (index) => {
    setForm((prev) => ({
      ...prev,
      travelManagers: (prev.travelManagers || []).filter(
        (_, managerIndex) => managerIndex !== index
      ),
    }));
  };

  const updateStepRow = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      stepsRows: prev.stepsRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row
      ),
    }));
  };

  const deleteStepRow = (index) => {
    setForm((prev) => ({
      ...prev,
      stepsRows: prev.stepsRows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleEditTripDate = (index) => {
    setEditingIndex(index);
    setForm(tripDateToForm(listTripDates[index]));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteTripDate = (index) => {
    setListTripDates((prev) => prev.filter((_, currentIndex) => currentIndex !== index));

    if (editingIndex === index) {
      resetForm();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex((prev) => (prev !== null ? prev - 1 : prev));
    }

    if (pendingNewIndex === index) {
      setPendingNewIndex(null);
    } else if (pendingNewIndex !== null && pendingNewIndex > index) {
      setPendingNewIndex((prev) => (prev !== null ? prev - 1 : prev));
    }
  };

  const buildTripDatePayload = () => {
    if (!form.startDate || !form.endDate) {
      showToast(t("tripPublication.publishTrip.toasts.requiredDates"), "error");
      return null;
    }

    if (duration.nbDays <= 0) {
      showToast(t("tripPublication.publishTrip.toasts.invalidDateRange"), "error");
      return null;
    }

    const hasStepDateOutOfRange = (form.stepsRows || []).some((row) => {
      const stepDate = (row?.date || "").slice(0, 10);
      if (!stepDate) return false;
      return stepDate < form.startDate || stepDate > form.endDate;
    });

    if (hasStepDateOutOfRange) {
      showToast(
        t("tripPublication.publishTrip.toasts.stepsDatesOutOfRange", {
          defaultValue:
            "Les dates des associations d'étapes doivent être incluses dans la période sélectionnée.",
        }),
        "error"
      );
      return null;
    }

    if (hasInvalidManagerPhone(form.travelManagers)) {
      showToast(
        t("tripPublication.publishTrip.toasts.invalidManagerPhone", {
          defaultValue: "Vérifiez le numéro de téléphone des managers du voyage.",
        }),
        "error"
      );
      return null;
    }

    const adultSeats = Number(form.adultSeats || 0);
    const childSeats = Number(form.childSeats || 0);
    const babySeats = Number(form.babySeats || 0);
    const existingTripDate = editingIndex !== null ? listTripDates[editingIndex] : null;

    return {
      ...(existingTripDate?.id ? { id: existingTripDate.id } : {}),
      startDate: form.startDate,
      endDate: form.endDate,
      nbDays: duration.nbDays,
      nbNight: duration.nbNight,
      isCheapestDate: Boolean(form.isCheapestDate),
      isFlightIncluded: false,
      adultPricePerPerson: {
        value: Number(form.adultPrice || 0),
        currency: "DH",
      },
      childPricePerPerson: {
        value: Number(form.childPrice || 0),
        currency: "DH",
      },
      babiesPricePerPerson: {
        value: Number(form.babyPrice || 0),
        currency: "DH",
      },
      stepsDate: buildStepsDate(form.stepsRows),
      flightItinerary: {
        outboundFlight: {
          isFlightWithStopover: false,
          flights: [],
        },
        returnFlight: {
          isFlightWithStopover: false,
          flights: [],
        },
      },
      seatsAvailable: {
        initialNumberOfSeatsAvailable: adultSeats,
        initialNumberOfChildrensSeatsAvailable: childSeats,
        initialNumberOfBabiessSeatsAvailable: babySeats,
        numberOfSeatsAvailable: adultSeats,
        numberOfChildrensSeatsAvailable: childSeats,
        numberOfBabiessSeatsAvailable: babySeats,
      },
      travelManagers: (form.travelManagers || [])
        .map((manager) => ({
          fullName: (manager?.fullName || "").trim(),
          email: (manager?.email || "").trim(),
          phoneNumber: {
            indicative: normalizeIndicative(manager?.phoneNumber?.indicative),
            number: (manager?.phoneNumber?.number || "").trim(),
          },
        }))
        .filter(
          (manager) => manager.fullName || manager.email || manager.phoneNumber.number
        ),
    };
  };

  const handleAddOrUpdateTripDate = () => {
    const tripDatePayload = buildTripDatePayload();
    if (!tripDatePayload) return;

    if (editingIndex === null) {
      setListTripDates((prev) => [...prev, tripDatePayload]);
    } else {
      setListTripDates((prev) =>
        prev.map((item, index) => (index === editingIndex ? tripDatePayload : item))
      );
      if (pendingNewIndex === editingIndex) {
        setPendingNewIndex(null);
      }
    }

    resetForm();
  };

  function buildTripPayload() {
    return {
      id: trip.id,
      childsMaxAge: parseFloat(maxChildAge),
      babiesMaxAge: parseFloat(maxBabyAge),
      tripId: trip.tripId,
      tripDates: cleanTripDates(listTripDates),
    };
  }

  const handleSaveTrip = async () => {
    if (!hasChanges) {
      navigate("/admin/circuits");
      return;
    }

    const isBlank = (value) =>
      value === null || value === undefined || String(value).trim() === "";
    const hasNoGeneralInfo = isBlank(maxChildAge) && isBlank(maxBabyAge);
    const hasNoTripDates = (listTripDates || []).length === 0;

    if (hasNoGeneralInfo && hasNoTripDates) {
      navigate("/admin/circuits");
      return;
    }

    const updatedTrip = buildTripPayload();
    const cleanedTrip = cleanObject(trip);
    const cleanedUpdatedTrip = cleanObject(updatedTrip);

    if (isEqual(cleanedTrip, cleanedUpdatedTrip)) {
      showToast(t("tripPublication.publishTrip.toasts.noChanges"), "info");
      return;
    }

    await runOperation(
      async () => {
        const { error: mutationError } = await updateTripPublication(
          updateTripPublicationMutation,
          trip.id,
          cleanedUpdatedTrip,
          accessToken
        );

        if (mutationError) {
          throw new Error(mutationError.message || t("tripPublication.publishTrip.errors.saveFailed"));
        }

        triggerRefresh();
        navigate("/admin/circuits");
      },
      {
        successMsg: t("tripPublication.publishTrip.toasts.saved"),
        errorMsg: t("tripPublication.publishTrip.toasts.saveError"),
      }
    );
  };

  const handlePublishTrip = async () => {
    const updatedTrip = buildTripPayload();
    const cleanedTrip = cleanObject(trip);
    const cleanedUpdatedTrip = cleanObject(updatedTrip);
    const tripChanged = !isEqual(cleanedTrip, cleanedUpdatedTrip);

    await runOperation(
      async () => {
        if (tripChanged) {
          const { error: updateError } = await updateTripPublication(
            updateTripPublicationMutation,
            trip.id,
            cleanedUpdatedTrip,
            accessToken
          );

          if (updateError) {
            throw new Error(updateError.message || t("tripPublication.publishTrip.errors.updateFailed"));
          }
        }

        const { error: publishError } = await publishTrip(
          publishTripMutation,
          trip.id,
          accessToken
        );

        if (publishError) {
          throw new Error(
            publishError.message || t("tripPublication.publishTrip.errors.publishFailed")
          );
        }

        triggerRefresh();
        showToast(t("tripPublication.publishTrip.toasts.published"), "success");
        navigate("/admin/circuits");
      },
      {
        successMsg: t("tripPublication.publishTrip.toasts.published"),
        errorMsg: t("tripPublication.publishTrip.toasts.publishError"),
      }
    );
  };

  const getCoverageLabel = (tripDate) =>
    isTripDateComplete(tripDate, trip?.trip?.tripProgramDetails)
      ? t("tripPublication.publishTrip.coverage.complete")
      : t("tripPublication.publishTrip.coverage.missing");

  const openUseTemplateModal = () => {
    setTemplateName("");
    setIsUseTemplateModalOpen(true);
  };

  const closeUseTemplateModal = () => {
    setIsUseTemplateModalOpen(false);
    setTemplateName("");
  };

  const handleUseTemplateSave = () => {
    closeUseTemplateModal();
  };

  if (loading) {
    return <div className={styles.stateMessage}>{t("tripPublication.publishTrip.states.loading")}</div>;
  }

  if (error || !trip?.id) {
    return <div className={styles.stateMessage}>{t("tripPublication.publishTrip.states.loadError")}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainCard}>
        <PublishTripHeader status={trip.status} onNewDeparture={handleNewDeparture} />

        {showEditNewStepAlert ? (
          <div className={styles.editAlert}>
            {t("tripPublication.publishTrip.alerts.newStepFromEdit", {
              defaultValue:
                "Une nouvelle étape a été ajoutée au circuit. Merci de la planifier dans les départs avant publication.",
            })}
          </div>
        ) : null}

        <GeneralSettingsSection
          tripReference={trip?.trip?.reference}
          maxChildAge={maxChildAge}
          maxBabyAge={maxBabyAge}
          onMaxChildAgeChange={setMaxChildAge}
          onMaxBabyAgeChange={setMaxBabyAge}
        />

        <TripDateFormSection
          key={formResetKey}
          editingIndex={editingIndex}
          form={form}
          stepOptions={stepOptions}
          duration={duration}
          currentInstanceLabel={
            editingIndex === null
              ? t("tripPublication.publishTrip.instanceNew")
              : t("tripPublication.publishTrip.instanceNumber", { number: editingIndex + 1 })
          }
          canDeleteCurrent={editingIndex !== null}
          onFormFieldChange={setFormField}
          onAddStepRow={addStepRow}
          onUpdateStepRow={updateStepRow}
          onDeleteStepRow={deleteStepRow}
          onAddManagerRow={addManagerRow}
          onUpdateManagerRow={updateManagerRow}
          onDeleteManagerRow={deleteManagerRow}
          onDeleteCurrent={() => {
            if (editingIndex === null) {
              resetForm();
              return;
            }
            handleDeleteTripDate(editingIndex);
          }}
          onSubmit={handleAddOrUpdateTripDate}
        />
      </div>

      <div className={styles.secondaryCard}>
        <TripDatesListSection
          tripDates={listTripDates}
          onEdit={handleEditTripDate}
          onDelete={handleDeleteTripDate}
          canDeleteTripDate={true}
          getCoverageLabel={getCoverageLabel}
        />

        <PublishTripActions
          canPublish={isAnyTripDateComplete}
          canUseAsTemplate={isAnyTripDateComplete}
          saveLabel={t(
            hasChanges
              ? "tripPublication.publishTrip.actions.saveTrip"
              : "tripPublication.publishTrip.actions.backToCircuits"
          )}
          saveIconName={hasChanges ? "iconPenToSquare" : "iconArrowLeft"}
          onSave={handleSaveTrip}
          onUseAsTemplate={openUseTemplateModal}
          onPublish={handlePublishTrip}
        />
      </div>

      <Modal t={t}
        isOpen={isUseTemplateModalOpen}
        onClose={closeUseTemplateModal}
        title={t("tripPublication.publishTrip.modals.useAsTemplateTitle")}
        size="sm"
        footer={
          <div className={styles.modalFooter}>
            <Button
              label={t("commons.buttons.cancel")}
              variant="secondary"
              onClick={closeUseTemplateModal}
            />
            <Button
              label={t("tripPublication.publishTrip.actions.saveTemplate")}
              variant="primary"
              onClick={handleUseTemplateSave}
            />
          </div>
        }
      >
        <div className={styles.modalBody}>
          <Input
            floatingLabel
            inputSize="medium"
            label={t("tripPublication.publishTrip.fields.templateName")}
            name="templateName"
            type="text"
            value={templateName}
            onChange={(event) => setTemplateName(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PublishTrip;
